import React, { useState } from 'react';
import { X, EnvelopeSimple, WhatsappLogo, ArrowSquareOut, Truck, FilePdf } from '@phosphor-icons/react';
import API from '@/lib/api';
import { toast } from 'sonner';

/**
 * VerzendMeldingModal — opent na succesvolle leverbon-verzending.
 *
 * Toont per leverbon de mogelijkheid om de klant op de hoogte te brengen
 * via Mail (open MailHub compose met prefill) of WhatsApp (wa.me deep-link).
 *
 * Props:
 *   leverbonnen: [{ nummer, klant_id, klant_naam, email, gsm, taal }]
 *   bedrijfsnaam: string
 *   onClose: () => void
 */

// Vertaalde sjablonen per taal
const SJABLONEN = {
  nl: {
    subject: (nr) => `Uw levering is verzonden — leverbon ${nr}`,
    body: (naam, nr, bedrijf) =>
      `Beste ${naam},\n\nUw levering is verzonden met leverbon nummer ${nr}.\n\nBij vragen ben ik bereikbaar via dit kanaal.\n\nMet vriendelijke groet,\n${bedrijf}`,
  },
  fr: {
    subject: (nr) => `Votre livraison est expédiée — bon de livraison ${nr}`,
    body: (naam, nr, bedrijf) =>
      `Cher/Chère ${naam},\n\nVotre livraison a été expédiée avec le bon de livraison numéro ${nr}.\n\nN'hésitez pas à me contacter pour toute question.\n\nCordialement,\n${bedrijf}`,
  },
  en: {
    subject: (nr) => `Your delivery has been shipped — delivery note ${nr}`,
    body: (naam, nr, bedrijf) =>
      `Dear ${naam},\n\nYour delivery has been shipped with delivery note number ${nr}.\n\nPlease feel free to contact me for any questions.\n\nKind regards,\n${bedrijf}`,
  },
  de: {
    subject: (nr) => `Ihre Lieferung wurde versandt — Lieferschein ${nr}`,
    body: (naam, nr, bedrijf) =>
      `Sehr geehrte/r ${naam},\n\nIhre Lieferung wurde mit Lieferschein-Nummer ${nr} versandt.\n\nBei Fragen stehe ich Ihnen jederzeit zur Verfügung.\n\nMit freundlichen Grüßen,\n${bedrijf}`,
  },
};

const _normaliseerGsm = (gsm) => {
  if (!gsm) return '';
  let n = (gsm + '').replace(/[^0-9+]/g, '');
  if (n.startsWith('00')) n = '+' + n.slice(2);
  if (n.startsWith('0') && !n.startsWith('+')) n = '+32' + n.slice(1);  // BE default
  return n.replace(/[^0-9]/g, '');
};

export default function VerzendMeldingModal({ leverbonnen, bedrijfsnaam, onClose }) {
  const [voegPdfToe, setVoegPdfToe] = useState(true);  // Bijlage-toggle (default AAN)
  const [busyWa, setBusyWa] = useState({});

  const getSjabloon = (taal) => SJABLONEN[taal] || SJABLONEN.nl;

  const openMail = (lb) => {
    if (!lb.email) {
      toast.error(`${lb.klant_naam} heeft geen e-mailadres in fiche`);
      return;
    }
    const tpl = getSjabloon(lb.taal);
    const subject = tpl.subject(lb.nummer);
    const body = tpl.body(lb.klant_naam, lb.nummer, bedrijfsnaam || '');
    // autoattach_doc_id zorgt dat MailHubPage de leverbon-PDF direct fetcht en
    // toevoegt aan de compose attachments-state (zie MailHubPage useEffect).
    const attachParam = voegPdfToe ? `&autoattach_doc_id=${encodeURIComponent(lb.nummer)}` : '';
    const url = `/mail?compose=1&to=${encodeURIComponent(lb.email)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}${attachParam}`;
    window.open(url, '_blank', 'noopener');
    toast.success(`Mail compose geopend voor ${lb.klant_naam}${voegPdfToe ? ' (PDF wordt automatisch bijgevoegd)' : ''}`);
  };

  const openWhatsApp = async (lb) => {
    const gsm = _normaliseerGsm(lb.gsm);
    if (!gsm || gsm.length < 9) {
      toast.error(`${lb.klant_naam} heeft geen geldig GSM-nummer in fiche`);
      return;
    }
    const tpl = getSjabloon(lb.taal);
    let body = tpl.body(lb.klant_naam, lb.nummer, bedrijfsnaam || '');
    // Vraag publieke PDF-link aan (geldig 30 dagen) en plak in tekst
    if (voegPdfToe) {
      setBusyWa(b => ({ ...b, [lb.nummer]: true }));
      try {
        const r = await API.post(`/documenten/${lb.nummer}/share-link?dagen=30`);
        if (r.data?.url) {
          body = `${body}\n\n📎 Leverbon-PDF: ${r.data.url}`;
        }
      } catch (e) {
        toast.warning('PDF-link kon niet gegenereerd worden, tekst wordt zonder bijlage verstuurd');
      }
      setBusyWa(b => ({ ...b, [lb.nummer]: false }));
    }
    // Open via WhatsApp Hub deep-link met prefill-tekst (betrouwbaarder dan wa.me direct)
    const hubUrl = `/whatsapp?klant_id=${encodeURIComponent(lb.klant_id)}&prefilltext=${encodeURIComponent(body)}`;
    window.open(hubUrl, '_blank', 'noopener');
    toast.success(`WhatsApp Hub geopend voor ${lb.klant_naam}${voegPdfToe ? ' met PDF-link' : ''}`);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[65] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="meld-verzend-modal"
    >
      <div
        className="bg-white border border-gray-200 shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-green-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck size={20} weight="duotone" className="text-emerald-700" />
            <div>
              <h3 className="text-base font-bold text-[#0A0A0A]">
                Verzending bevestigd — klant op de hoogte brengen?
              </h3>
              <p className="text-xs text-gray-600 mt-0.5">
                Stuur een melding via mail of WhatsApp met het leverbon-nummer.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-emerald-100 rounded"
            data-testid="meld-verzend-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-4 space-y-3">
          {leverbonnen.map(lb => (
            <div
              key={lb.nummer}
              className="border border-gray-200 rounded p-3 bg-gray-50"
              data-testid={`meld-verzend-row-${lb.nummer}`}
            >
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] uppercase font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                    {lb.nummer}
                  </span>
                  <span className="font-bold text-gray-900 truncate">{lb.klant_naam}</span>
                  {lb.taal && lb.taal !== 'nl' && (
                    <span className="text-[10px] bg-blue-100 text-blue-700 px-1 rounded uppercase">
                      {lb.taal}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[11px] text-gray-500">
                  <span className={lb.email ? '' : 'text-gray-300'}>
                    ✉️ {lb.email || 'geen email'}
                  </span>
                  <span className={lb.gsm ? '' : 'text-gray-300'}>
                    📱 {lb.gsm || 'geen GSM'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => openMail(lb)}
                  disabled={!lb.email}
                  className="flex-1 min-w-[160px] px-3 py-2 bg-[#002FA7] hover:bg-[#001F70] disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-bold rounded inline-flex items-center justify-center gap-2 transition-colors"
                  title={lb.email ? `Open mail compose voor ${lb.email}` : 'Geen e-mailadres in klantfiche'}
                  data-testid={`meld-verzend-mail-${lb.nummer}`}
                >
                  <EnvelopeSimple size={16} weight="fill" />
                  Stuur via Mail
                  <ArrowSquareOut size={12} weight="bold" />
                </button>
                <button
                  onClick={() => openWhatsApp(lb)}
                  disabled={!lb.gsm || busyWa[lb.nummer]}
                  className="flex-1 min-w-[160px] px-3 py-2 bg-[#25D366] hover:bg-[#1FAD55] disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-bold rounded inline-flex items-center justify-center gap-2 transition-colors"
                  title={lb.gsm ? `Open WhatsApp voor ${lb.gsm}` : 'Geen GSM-nummer in klantfiche'}
                  data-testid={`meld-verzend-wa-${lb.nummer}`}
                >
                  <WhatsappLogo size={16} weight="fill" />
                  {busyWa[lb.nummer] ? 'PDF-link...' : 'Stuur via WhatsApp'}
                  <ArrowSquareOut size={12} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-between items-center flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer" data-testid="meld-verzend-pdf-toggle-label">
              <input
                type="checkbox"
                checked={voegPdfToe}
                onChange={e => setVoegPdfToe(e.target.checked)}
                className="w-3.5 h-3.5"
                data-testid="meld-verzend-pdf-toggle"
              />
              <FilePdf size={12} weight="duotone" className="text-red-600" />
              <span className="font-semibold">Leverbon-PDF meesturen</span>
              <span className="text-[10px] text-gray-500">
                (mail: als bijlage · whatsapp: download-link 30d geldig)
              </span>
            </label>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-gray-500 italic">
              💡 Sjabloon auto-vertaald naar klant-taal
            </span>
            <button
              onClick={onClose}
              className="btn btn-ghost btn-sm"
              data-testid="meld-verzend-skip"
            >
              Overslaan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
